import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { withStyles, CircularProgress, Divider, Typography, IconButton, Button } from '@material-ui/core';
import { Info } from '@material-ui/icons';

import Selector from '../../common/Selector';
import InfoModal from './InfoModal';
import AdditionalCourseDialog from './AdditionalCourseDialog';
// import { headerButtonStyles } from '../../../collums-constants/styles/stylesheets/buttonsStyles';
import {
    PriceText,
    DurationText,
    DurationContainer,
    ClockIcon,
    PriceChevronContainer,
    ChevronIcon
} from '../common/StyledText';

import ServicesApi from '../../../api/servicesApi';
import { STAFF, CONFIRM } from '../../../constants/steps';
import {
    getCurrentAppointments,
    getCurrentCategory,
    getCurrentLocation,
    getCourseContext
} from '../../../redux/selectors/book-appointment';
import {
    changeService,
    changeStep,
    setCurrentCategory,
    updateAppointments,
    updateGiftVouchers
} from '../../../redux/actions/book-appointment';
import { toLocaleString } from '../../../collums-components/helpers';

import { serviceStyles } from './styles';
import { SERVICE_AVAILABILITIES, GIFT_VOUCHER_AMOUNT } from '../../../collums-constants';
import { getLocationItem } from '../../../collums-constants/utils';
import GiftVoucherDialog from '../GiftVoucherDialog';
import BlockedDialog from '../../common/Dialog';
// import { DURATION_IO_FROM_FRONT } from '../../../constants/socket';

const SERVICE_VIEW = {
    SERVICES: 'SERVICES',
    COURSES: 'COURSES',
    GIFT_VOUCHER: 'GIFT_VOUCHER'
};

function Service({ classes, serviceIndex, forceCourseView }) {
    // const [savedCategories, setSavedCategories] = useState([]);
    const savedHaveMore = useRef(false);
    const haveMore = useRef(true);
    const dispatch = useDispatch();
    const makingRequest = useRef(false);
    const [skipServices, setSkipServices] = useState(0);
    const [skipCategories, setSkipCategories] = useState(0);
    const currentAppts = useSelector(getCurrentAppointments);
    const currentLocation = useSelector(getCurrentLocation);
    const courseContext = useSelector(getCourseContext);
    const [options, setOptions] = useState([]);
    const [stateCourseSubCategories, setStateCourseSubCategories] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [modalProps, setModalProps] = useState({
        title: '',
        content: '',
        isVisible: false
    });
    const [isLoadingMore, setLoadingMore] = useState(false);
    const [serviceView, setServiceView] = useState(forceCourseView ? SERVICE_VIEW.COURSES : SERVICE_VIEW.SERVICES);
    const history = useHistory();
    const { url } = useRouteMatch();
    // const buttonClasses = makeStyles(headerButtonStyles)();
    const organisation = useSelector(state => state.auth.organisation);

    const currentCategory = useSelector(getCurrentCategory);
    const skipNextFillServices = useRef(false);
    const [showAddAnotherCourseDialog, setShowAddAnotherCourseDialog] = useState(false);
    const [giftVoucherValue, setGiftVoucherValue] = useState(0);

    const urlSearchParams = new URLSearchParams(window.location.search);
    const viewParam = urlSearchParams.get('view');
    const shouldShowCourses = viewParam === 'courses';
    const shouldShowServices = viewParam === 'services';
    const isCourseBooking = urlSearchParams.get('course') === 'true';
    const isCourseServiceBooking = urlSearchParams.get('courseServiceBooking') === 'true';

    const buildUrlWithCourseParams = (baseUrl, additionalParams = '') => {
        const params = new URLSearchParams();

        if (additionalParams.includes('service=')) {
            const serviceMatch = additionalParams.match(/service=([^&]*)/);
            if (serviceMatch) params.set('service', serviceMatch[1]);
        }

        if (additionalParams.includes('view=')) {
            const viewMatch = additionalParams.match(/view=([^&]*)/);
            if (viewMatch) params.set('view', viewMatch[1]);
        }

        if (isCourseBooking) params.set('course', 'true');
        if (isCourseServiceBooking) params.set('courseServiceBooking', 'true');

        const queryString = params.toString();
        return queryString ? `${baseUrl}?${queryString}` : baseUrl;
    };

    const { hasRegularServices, hasCoursePurchases, hasCourseDerivedServices } = useMemo(() => {
        const courseServices = courseContext?.courseServices || [];
        const regular = currentAppts.some(appt => {
            const serviceId = appt.service?.id || appt.service?._id;
            const isCourseServiceId = courseServices.includes(serviceId);
            const isRegularBooking = appt.service?._isRegularServiceBooking;

            return !appt.service?.salePrice && (!isCourseServiceId || isRegularBooking);
        });

        const purchases = currentAppts.some(appt => appt.service?.salePrice);

        const derived = currentAppts.some(appt => {
            const serviceId = appt.service?.id || appt.service?._id;
            const isCourseServiceId = courseServices.includes(serviceId);
            const isRegularBooking = appt.service?._isRegularServiceBooking;

            return isCourseServiceId && !isRegularBooking;
        });

        return {
            hasRegularServices: regular,
            hasCoursePurchases: purchases,
            hasCourseDerivedServices: derived
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentAppts, courseContext?.courseServices]);

    const hasServices = hasRegularServices || hasCourseDerivedServices;
    const hasCourses = hasCoursePurchases || hasCourseDerivedServices;
    // Memoized view determination logic
    const { canSwitchViews, lockedToServices, lockedToCourses } = useMemo(() => {
        const hasAddedServices = currentAppts && currentAppts.length > 0;

        const canSwitch =
            !shouldShowCourses &&
            !shouldShowServices &&
            !currentCategory &&
            !hasAddedServices &&
            !(hasRegularServices && (hasCoursePurchases || hasCourseDerivedServices));

        const lockToServices =
            (hasRegularServices && !hasCoursePurchases && !hasCourseDerivedServices && !shouldShowCourses) ||
            shouldShowServices ||
            hasAddedServices;
        const lockToCourses = (hasCoursePurchases && !hasRegularServices && !shouldShowCourses) || shouldShowCourses;

        return {
            canSwitchViews: canSwitch,
            lockedToServices: lockToServices,
            lockedToCourses: lockToCourses
        };
    }, [
        shouldShowCourses,
        shouldShowServices,
        currentCategory,
        hasRegularServices,
        hasCoursePurchases,
        hasCourseDerivedServices,
        currentAppts
    ]);

    const correctServiceView = useMemo(() => {
        return lockedToCourses ? SERVICE_VIEW.COURSES : lockedToServices ? SERVICE_VIEW.SERVICES : serviceView;
    }, [lockedToCourses, lockedToServices, serviceView]);

    useEffect(() => {
        if (correctServiceView !== serviceView) {
            setServiceView(correctServiceView);
        }
    }, [
        correctServiceView,
        serviceView,
        hasRegularServices,
        hasCoursePurchases,
        hasCourseDerivedServices,
        currentAppts.length
    ]);

    useEffect(() => {
        if (forceCourseView) {
            setServiceView(SERVICE_VIEW.COURSES);
        }
    }, [forceCourseView]);

    const openModal = data => {
        setModalProps({
            isVisible: true,
            title: data.title,
            content: data.content
        });
    };

    const handleAddAnotherCourseConfirm = () => {
        setShowAddAnotherCourseDialog(false);
        dispatch(setCurrentCategory(null));
        setSkipCategories(0);

        const urlParts = url.split('/');
        if (urlParts.length >= 3) {
            history.push(buildUrlWithCourseParams(`/step/${urlParts[2]}`, `service=${serviceIndex}&view=courses`));
        }
    };

    const handleAddAnotherCourseCancel = () => {
        setShowAddAnotherCourseDialog(false);
        dispatch(changeStep(CONFIRM));
    };

    const user = useSelector(state => state.auth.user);
    const [blockedDialogVisible, setBlockedDialogVisible] = useState(false);

    const handleCurrentOption = async opt => {
        const urlParts = url.split('/');
        if (!opt) {
            dispatch(setCurrentCategory(null));
            return;
        }

        if (correctServiceView === SERVICE_VIEW.SERVICES && user?.blockOnlineBooking) {
            setBlockedDialogVisible(true);
            return;
        }

        const isSelectingCourse =
            correctServiceView === SERVICE_VIEW.COURSES && currentCategory && opt.salePrice !== undefined;
        // const isSelectingService = correctServiceView === SERVICE_VIEW.SERVICES && opt.salePrice === undefined;

        // Allow mixing course-derived services with regular services
        // Course-derived services are still services, just with different pricing
        // Only prevent true course purchases (with sale prices) from mixing with services
        if (isSelectingCourse && hasServices) {
            console.warn('Cannot mix course purchases with individual services');
            return;
        }

        if (correctServiceView === SERVICE_VIEW.COURSES) {
            if (!currentCategory) {
                dispatch(setCurrentCategory(opt));
                history.push(buildUrlWithCourseParams(`${url}/${opt.id}`, `service=${serviceIndex}`));
                return;
            }

            if (opt.parentCategory) {
                dispatch(setCurrentCategory(opt));
                const urlParts = url.split('/');
                setOptions([]);
                urlParts[3] = opt.id;
                history.push(`${urlParts.join('/')}?service=${serviceIndex}`);
                return;
            }

            try {
                const urlParts = url.split('/');
                urlParts[3] = urlParts[3] + '/' + opt.id;
                history.push(`${urlParts.join('/')}?service=${serviceIndex}`);
                const tempCurrAppts = [...currentAppts];
                tempCurrAppts.push({ service: opt, staff: null });
                dispatch(updateAppointments(tempCurrAppts));

                setShowAddAnotherCourseDialog(true);
            } catch (e) {
                console.error('Failed to add course to appointments', e);
            }
            return;
        }

        if (!currentCategory) {
            dispatch(setCurrentCategory(opt));
            history.push(buildUrlWithCourseParams(`${url}/${opt.id}`, `service=${serviceIndex}`));
            return;
        }

        // socket.send({
        //     type: DURATION_IO_FROM_FRONT,
        //     payload: opt.defaultDuration
        // });

        if (!opt.availability && urlParts.length === 4) {
            urlParts[3] = opt.id;
            dispatch(setCurrentCategory(opt));
            setSkipServices(0);
            history.push(buildUrlWithCourseParams(urlParts.join('/'), `service=${serviceIndex}`));
        } else {
            let tempCurrAppts = [...currentAppts];
            const serviceId = opt.id || opt._id;
            const isCourseServiceId = courseContext?.courseServices?.includes(serviceId);

            // Check if this is a course-derived service (first booking) or regular service (subsequent bookings)
            const existingBookingsOfThisService = currentAppts.filter(
                appt => appt.service?.id === serviceId || appt.service?._id === serviceId
            ).length;

            // First booking of a course service = course-derived, subsequent bookings = regular
            // const isCourseDerivedService = isCourseServiceId && existingBookingsOfThisService === 0;
            const isRegularService = !isCourseServiceId || existingBookingsOfThisService > 0;

            const appointmentService = {
                ...opt,
                _isRegularServiceBooking: isRegularService
            };

            if (currentAppts.length - 1 < Number(serviceIndex)) {
                tempCurrAppts.push({
                    service: appointmentService,
                    staff: null
                });
            } else {
                tempCurrAppts[Number(serviceIndex)] = {
                    service: appointmentService,
                    staff: null
                };
            }

            tempCurrAppts = tempCurrAppts.map((appt, index) => {
                const apptServiceId = appt.service?.id || appt.service?._id;
                if (apptServiceId === serviceId) {
                    const occurrencesBefore = tempCurrAppts
                        .slice(0, index)
                        .filter(prevAppt => (prevAppt.service?.id || prevAppt.service?._id) === serviceId).length;

                    const isFirstOccurrence = occurrencesBefore === 0;
                    const shouldBeCourseDerived = isCourseServiceId && isFirstOccurrence;

                    return {
                        ...appt,
                        service: {
                            ...appt.service,
                            _isRegularServiceBooking: !shouldBeCourseDerived
                        }
                    };
                }
                return appt;
            });

            dispatch(updateAppointments(tempCurrAppts));
            dispatch(changeService(appointmentService));

            dispatch(changeStep(STAFF));
            history.push(buildUrlWithCourseParams(`${url}/${opt.id}`, `service=${serviceIndex}`));
        }
    };

    async function fillCategories(forceSkipTo0 = false) {
        if (!currentLocation?.id) return;
        let skipCategoriesNumber = skipCategories;
        if (forceSkipTo0) {
            skipCategoriesNumber = 0;
        }
        const currentSkipCategories = skipCategoriesNumber;
        const maxSize = 1500;
        if (skipCategoriesNumber === 0) {
            setIsLoading(true);
        } else {
            setLoadingMore(true);
        }
        try {
            const serviceCategories = (
                await ServicesApi.getCategories(currentLocation.id, skipCategoriesNumber, maxSize)
            ).filter(cat => !cat.parentCategory);
            const coursesData = ((await ServicesApi.getCoursesByCategory(currentLocation.id)) || []).sort(
                (a, b) => a.category.order - b.category.order
            );
            let courseCategories = coursesData
                .filter(item => {
                    return item?.courses !== undefined || (item?.category && item.category.showOnline);
                })
                .map(item => item?.category);
            const courseSubCategories = courseCategories.filter(el => el.parentCategory);
            courseCategories = courseCategories
                .filter(el => !el.parentCategory)
                .map(cat => {
                    const subCategories = (cat.subCategories || [])
                        .filter(sCatId =>
                            courseSubCategories.find(courseSubcategory => courseSubcategory.id === sCatId)
                        )
                        .map(sCatId => courseSubCategories.find(courseSubcategory => courseSubcategory.id === sCatId));
                    return { ...cat, subCategories };
                });
            setStateCourseSubCategories(courseSubCategories);

            let res = correctServiceView === SERVICE_VIEW.COURSES ? courseCategories : serviceCategories;

            if (skipCategoriesNumber === 0) {
                setOptions(res);
                setIsLoading(false);
            } else {
                const filteredCategories = res.filter(category => {
                    return options.find(element => element.id === category.id) === undefined;
                });
                if (filteredCategories.length > 0) {
                    const newOptions = [...options, ...filteredCategories];
                    setOptions(newOptions);
                }
                setLoadingMore(false);
            }
            setSkipCategories(res.length + skipCategoriesNumber);
            if (res.length < maxSize) {
                haveMore.current = false;
            } else haveMore.current = true;
            makingRequest.current = false;

            if (currentSkipCategories === 0) {
                // scrollToSteps();
            }
        } catch (err) {
            haveMore.current = false;
            setLoadingMore(false);
            setIsLoading(false);
        }
    }

    const scrollToSteps = () => {
        const stepElement = document.querySelector('.MuiStepper-root');
        if (stepElement) {
            setTimeout(() => {
                stepElement.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start'
                });
            }, 100);
        }
    };

    async function fillServices() {
        const maxSize = 20;
        const currentSkipServices = skipServices;
        if (skipServices === 0) {
            setIsLoading(true);
        } else {
            setLoadingMore(true);
        }
        try {
            let res = [];
            if (correctServiceView === SERVICE_VIEW.COURSES) {
                const coursesData = (await ServicesApi.getCoursesByCategory(currentLocation.id)).sort(
                    (a, b) => a.category.order - b.category.order
                );

                const resCourses = coursesData.find(coursegroup => coursegroup.category.id === currentCategory.id);
                res = resCourses?.courses || [];
                setOptions([
                    ...currentCategory.subCategories
                        .map(catId => stateCourseSubCategories.find(sub => sub.id === catId) || catId)
                        .filter(el => el.id),
                    ...res
                ]); // subcategories here
                setIsLoading(false);
            } else {
                res = await ServicesApi.getServices(currentLocation.id, currentCategory.id, skipServices, maxSize);
                const filteredServices = res.filter(
                    service =>
                        service.onlineName !== undefined && service.availability !== SERVICE_AVAILABILITIES.BOOK_ONLY
                );
                const servicesWithSubcategories = [
                    ...currentCategory.subCategories
                        .filter(cat => typeof cat !== 'string')
                        .filter(cat => {
                            return cat.locations.find(loc => loc.clinic === currentLocation.id)?.showOnline;
                        }),
                    ...filteredServices
                ];
                if (skipServices === 0) {
                    if (![...currentCategory.subCategories].find(cat => typeof cat === 'string')) {
                        setOptions(servicesWithSubcategories);
                        setIsLoading(false);
                    }
                } else {
                    const newOptions = [...options, ...filteredServices];
                    setOptions(newOptions);
                }
            }

            setLoadingMore(false);
            setSkipServices(res.length + skipServices);
            if (res.length < maxSize) {
                haveMore.current = false;
            } else haveMore.current = true;
            makingRequest.current = false;
            if (currentSkipServices === 0) {
                scrollToSteps();
            }
        } catch (err) {
            haveMore.current = false;
            setLoadingMore(false);
            setIsLoading(false);
        }
    }

    useEffect(() => {
        if (correctServiceView === SERVICE_VIEW.COURSES && hasCourses && !currentCategory) {
            setSkipServices(0);
            setSkipCategories(0);
            fillCategories(true);
            return;
        }

        if (correctServiceView === SERVICE_VIEW.COURSES && skipNextFillServices.current) {
            skipNextFillServices.current = false;
            return;
        }

        if (currentCategory && currentLocation.id) {
            savedHaveMore.current = haveMore;
            if (correctServiceView === SERVICE_VIEW.COURSES) {
                setTimeout(() => {
                    fillServices();
                }, 300);
            } else {
                setTimeout(() => {
                    fillServices();
                }, 300);
            }
        } else {
            setSkipServices(0);
            setSkipCategories(0);
            fillCategories(true);
        }
        /*eslint-disable-next-line */
    }, [currentCategory, currentLocation.id, correctServiceView]);

    // Course category locations don't carry onlineName/onlineDescription/onlineSubtitle —
    // those fields are stored at the document root only. Detect them via `subCategories`
    // (course categories have it, courses do not) so we can fall back to the root values.
    const isCourseCategoryOption = option =>
        correctServiceView === SERVICE_VIEW.COURSES && option?.subCategories !== undefined;

    const getOnlineField = (option, fieldName) => {
        const locationValue = getLocationItem(option, currentLocation.id)?.[fieldName];
        if (locationValue) return locationValue;
        if (isCourseCategoryOption(option)) return option[fieldName];
        return undefined;
    };

    const handleInfoClick = (event, option) => {
        event.stopPropagation();
        openModal({
            isVisible: true,
            content: getOnlineField(option, 'onlineDescription') || '',
            title: getOnlineField(option, 'onlineName') || option.name
        });
    };

    useEffect(() => {
        const handleScroll = () => {
            if (makingRequest.current || !haveMore.current) return;
            const scrollTop = window.scrollY || document.documentElement.scrollTop;
            const scrollHeight = document.documentElement.scrollHeight;
            const clientHeight = document.documentElement.clientHeight;
            if (scrollTop + clientHeight >= scrollHeight * 0.99) {
                makingRequest.current = true;
                if (!currentCategory) {
                    fillCategories();
                } else {
                    setTimeout(() => {
                        fillServices();
                    }, 300);
                }
            }
        };
        window.addEventListener('scroll', handleScroll);

        // If content fits in viewport (no scrollbar), load more automatically
        const rafId = requestAnimationFrame(() => {
            if (!makingRequest.current && haveMore.current && options.length > 0) {
                const scrollHeight = document.documentElement.scrollHeight;
                const clientHeight = document.documentElement.clientHeight;
                if (scrollHeight <= clientHeight) {
                    makingRequest.current = true;
                    if (!currentCategory) {
                        fillCategories();
                    } else {
                        setTimeout(() => {
                            fillServices();
                        }, 300);
                    }
                }
            }
        });

        return () => {
            window.removeEventListener('scroll', handleScroll);
            cancelAnimationFrame(rafId);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentCategory, options, skipCategories, skipServices]);

    const printDuration = loc => {
        if (!loc || !loc.staffs || !Array.isArray(loc.staffs)) {
            const defaultDuration = loc?.defaultDuration;
            if (!isFinite(defaultDuration)) {
                return null;
            }
            return defaultDuration;
        }

        const durations = loc.staffs.map(staff => {
            if (staff.duration) {
                return staff.duration;
            }
            return loc?.defaultDuration;
        });

        const validDurations = durations.filter(duration => isFinite(duration) && duration != null);

        if (validDurations.length === 0) {
            return null;
        }
        const minDuration = Math.min(...validDurations);
        const maxDuration = Math.max(...validDurations);
        return (minDuration !== maxDuration ? 'From ' : '') + minDuration;
    };

    const handleGiftVoucherSelect = (value, receiver = {}) => {
        dispatch(updateGiftVouchers([{ value, receiver }]));
        dispatch(changeStep(CONFIRM));
    };

    return (
        <div className={classes.servicesRoot}>
            {isLoading ? (
                <CircularProgress />
            ) : (
                <>
                    <div className={classes.switchButtonsWrapper}>
                        <Button
                            disableElevation
                            disableRipple
                            disabled={serviceView === SERVICE_VIEW.SERVICES || !canSwitchViews}
                            className={
                                serviceView === SERVICE_VIEW.SERVICES
                                    ? `${classes.switchButton} ${classes.switchButtonSelected}`
                                    : classes.switchButton
                            }
                            onClick={() => {
                                if (canSwitchViews) {
                                    setServiceView(SERVICE_VIEW.SERVICES);
                                    dispatch(setCurrentCategory(null));
                                    dispatch(updateAppointments([]));
                                    setOptions([]);
                                    setSkipCategories(0);
                                    const urlParts = url.split('/');
                                    if (urlParts.length >= 3) {
                                        history.push(
                                            buildUrlWithCourseParams(`/step/${urlParts[2]}`, `service=${serviceIndex}`)
                                        );
                                    }
                                }
                            }}
                        >
                            Services
                        </Button>
                        <Button
                            disableElevation
                            disableRipple
                            disabled={serviceView === SERVICE_VIEW.COURSES || !canSwitchViews}
                            className={
                                serviceView === SERVICE_VIEW.COURSES
                                    ? `${classes.switchButton} ${classes.switchButtonSelected}`
                                    : classes.switchButton
                            }
                            onClick={() => {
                                if (canSwitchViews) {
                                    setServiceView(SERVICE_VIEW.COURSES);
                                    dispatch(setCurrentCategory(null));
                                    dispatch(updateAppointments([]));
                                    setOptions([]);
                                    setSkipCategories(0);
                                    const urlParts = url.split('/');
                                    if (urlParts.length >= 3) {
                                        history.push(
                                            buildUrlWithCourseParams(`/step/${urlParts[2]}`, `service=${serviceIndex}`)
                                        );
                                    }
                                }
                            }}
                        >
                            Courses
                        </Button>
                        {organisation?.sellGiftVoucherOnline && (
                            <Button
                                disabled={serviceView === SERVICE_VIEW.GIFT_VOUCHER || !canSwitchViews}
                                className={
                                    serviceView === SERVICE_VIEW.GIFT_VOUCHER
                                        ? `${classes.switchButton} ${classes.switchButtonSelected}`
                                        : classes.switchButton
                                }
                                onClick={() => {
                                    if (canSwitchViews) {
                                        setServiceView(SERVICE_VIEW.GIFT_VOUCHER);
                                    }
                                }}
                            >
                                Gift voucher
                            </Button>
                        )}
                    </div>

                    <Selector className="overflowVisible" id="Scrollable">
                        {isLoading || serviceView === SERVICE_VIEW.GIFT_VOUCHER ? null : options &&
                          options.length > 0 ? (
                            options.map(option => {
                                return (
                                    <React.Fragment key={option.id}>
                                        <Selector.Row onClick={() => handleCurrentOption(option)}>
                                            <Selector.Column>
                                                <div style={{ display: 'flow-root' }}>
                                                    <Typography className={classes.name} variant="h4">
                                                        {getOnlineField(option, 'onlineName') || option.name}
                                                    </Typography>
                                                    {getOnlineField(option, 'onlineDescription') && (
                                                        <IconButton
                                                            onClick={event => handleInfoClick(event, option)}
                                                            className={classes.info}
                                                        >
                                                            <Info className={classes.infoIcon} />
                                                        </IconButton>
                                                    )}
                                                </div>
                                                {getOnlineField(option, 'onlineSubtitle') && (
                                                    <span className={classes.durationText}>
                                                        {getOnlineField(option, 'onlineSubtitle')}
                                                    </span>
                                                )}
                                                {getLocationItem(option, currentLocation.id)?.defaultDuration &&
                                                    option.showDurationOnline &&
                                                    (() => {
                                                        const durationValue = printDuration(
                                                            getLocationItem(option, currentLocation.id)
                                                        );

                                                        // Don't render duration if it's null (indicating Infinity or invalid)
                                                        if (durationValue === null) {
                                                            return null;
                                                        }

                                                        return (
                                                            <DurationContainer>
                                                                <ClockIcon />
                                                                <DurationText>{durationValue} mins</DurationText>
                                                            </DurationContainer>
                                                        );
                                                    })()}
                                            </Selector.Column>
                                            <PriceChevronContainer>
                                                {option.showPriceOnline && option.metadata && (
                                                    <PriceText>
                                                        {option.metadata.lowestPrice >= 0 &&
                                                        option.metadata.highestPrice >= 0 &&
                                                        option.metadata.lowestPrice !== option.metadata.highestPrice
                                                            ? 'From '
                                                            : ''}
                                                        {toLocaleString(option.metadata.price)}
                                                    </PriceText>
                                                )}
                                                {option.salePrice !== undefined && (
                                                    <PriceText>{toLocaleString(option.salePrice)}</PriceText>
                                                )}
                                                {option.showPriceOnline &&
                                                    option.defaultPrice !== undefined &&
                                                    !option.metadata &&
                                                    option.salePrice === undefined && (
                                                        <PriceText>
                                                            {`From ${toLocaleString(option.defaultPrice)}`}
                                                        </PriceText>
                                                    )}
                                                <ChevronIcon />
                                            </PriceChevronContainer>
                                        </Selector.Row>
                                        <Divider />
                                    </React.Fragment>
                                );
                            })
                        ) : (
                            <Typography style={{ padding: 10, textAlign: 'center' }}>
                                {currentCategory
                                    ? correctServiceView === SERVICE_VIEW.COURSES
                                        ? 'There are no courses in this category'
                                        : 'There are no services currently available'
                                    : correctServiceView === SERVICE_VIEW.COURSES
                                    ? 'There are no course categories currently available'
                                    : 'Online booking is currently unavailable'}
                            </Typography>
                        )}
                        {serviceView === SERVICE_VIEW.GIFT_VOUCHER
                            ? (Array.isArray(organisation?.giftVoucherValues) && organisation?.giftVoucherValues.length
                                  ? organisation?.giftVoucherValues
                                  : GIFT_VOUCHER_AMOUNT
                              ).map(option => {
                                  return (
                                      <React.Fragment key={option.id}>
                                          <Selector.Row onClick={() => setGiftVoucherValue(option)}>
                                              <Selector.Column>
                                                  <div style={{ display: 'flow-root' }}>
                                                      <Typography
                                                          className={classes.name}
                                                          variant="h4"
                                                          style={{ marginTop: 10 }}
                                                      >
                                                          Gift voucher
                                                      </Typography>
                                                  </div>
                                              </Selector.Column>
                                              <PriceChevronContainer>
                                                  <PriceText>{toLocaleString(option)}</PriceText>
                                                  <ChevronIcon />
                                              </PriceChevronContainer>
                                          </Selector.Row>
                                          <Divider />
                                      </React.Fragment>
                                  );
                              })
                            : null}
                        {isLoadingMore && <CircularProgress />}
                    </Selector>
                    <InfoModal
                        setIsVisible={setModalProps}
                        isVisible={modalProps.isVisible}
                        title={modalProps.title}
                        content={modalProps.content}
                    />
                    <AdditionalCourseDialog
                        open={showAddAnotherCourseDialog}
                        onConfirm={handleAddAnotherCourseConfirm}
                        onCancel={handleAddAnotherCourseCancel}
                        title="Do you want to add another course?"
                    />
                    <GiftVoucherDialog
                        open={giftVoucherValue}
                        onCancel={() => {
                            setGiftVoucherValue(0);
                        }}
                        onConfirm={receiver => {
                            handleGiftVoucherSelect(giftVoucherValue, receiver);
                            setGiftVoucherValue(0);
                        }}
                    />
                    <BlockedDialog
                        title={''}
                        maxWidth={'xs'}
                        content={
                            user?.blockOnlineText || 'You are not allowed to book online. Please contact the clinic.'
                        }
                        isVisible={blockedDialogVisible}
                        setVisibility={() => setBlockedDialogVisible(false)}
                        actionOnContinue={() => setBlockedDialogVisible(false)}
                        customActionName="Close"
                    />
                </>
            )}
        </div>
    );
}

Service.propTypes = {
    classes: PropTypes.object.isRequired,
    serviceIndex: PropTypes.number
};

export default withStyles(serviceStyles)(Service);
